import React, { useState } from 'react';
import { 
  Database, Cloud, Webhook, Key, Save, CheckCircle, Eye, EyeOff
} from 'lucide-react';
import GoogleCalendarIntegration from './GoogleCalendarIntegration';
// Import du composant ImportWizard (chemin corrigé)
import ImportWizard from '../ImportWizard';

const IntegrationsSettings = () => {
  const [saved, setSaved] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    opensky_enabled: true,
    opensky_username: '',
    opensky_password: '',
    flightaware_enabled: false,
    flightaware_api_key: '',
    webhook_url: '',
    webhook_events: ['landing', 'takeoff', 'fuel_sale']
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* DATA IMPORT WIZARD - NOUVEAU */}
      {/* ============================================ */}
      <ImportWizard />

      {/* ============================================ */}
      {/* GOOGLE CALENDAR INTEGRATION */}
      {/* ============================================ */}
      <GoogleCalendarIntegration />

      {/* ============================================ */}
      {/* OTHER INTEGRATIONS */}
      {/* ============================================ */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Other Integrations</h3>
            <p className="text-gray-400 text-sm">Connect external services and APIs</p>
          </div>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
          >
            <Save className="w-4 h-4" />
            Save Integrations
          </button>
        </div>

        {saved && (
          <div className="mb-4 p-3 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30">
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Integration settings saved successfully!
          </div>
        )}

        <div className="space-y-6">
          {/* OpenSky */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Cloud className="w-5 h-5 text-blue-400" />
                <h4 className="text-white font-medium">OpenSky Network</h4>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.opensky_enabled}
                  onChange={(e) => setSettings({ ...settings, opensky_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
            {settings.opensky_enabled && (
              <div className="space-y-3 pl-8">
                <input
                  type="text"
                  placeholder="Username"
                  value={settings.opensky_username}
                  onChange={(e) => setSettings({ ...settings, opensky_username: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={settings.opensky_password}
                  onChange={(e) => setSettings({ ...settings, opensky_password: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
              </div>
            )}
          </div>

          {/* FlightAware */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-green-400" />
                <h4 className="text-white font-medium">FlightAware AeroAPI</h4>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.flightaware_enabled}
                  onChange={(e) => setSettings({ ...settings, flightaware_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
            {settings.flightaware_enabled && (
              <div className="space-y-3 pl-8">
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="API Key"
                    value={settings.flightaware_api_key}
                    onChange={(e) => setSettings({ ...settings, flightaware_api_key: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm pr-10"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Webhook */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Webhook className="w-5 h-5 text-yellow-400" />
              <h4 className="text-white font-medium">Webhook Endpoint</h4>
            </div>
            <div className="space-y-3">
              <input
                type="url"
                placeholder="https://your-server.com/webhook"
                value={settings.webhook_url}
                onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
              />
              <div className="flex flex-wrap gap-3">
                {['landing', 'takeoff', 'fuel_sale', 'new_visitor', 'parking_start', 'parking_end'].map((event) => (
                  <label key={event} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.webhook_events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings({ ...settings, webhook_events: [...settings.webhook_events, event] });
                        } else {
                          setSettings({ ...settings, webhook_events: settings.webhook_events.filter(e => e !== event) });
                        }
                      }}
                      className="rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300 capitalize">{event.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSettings;